class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Reset infinite scroll state when filters/sorting change
    FacetFiltersForm.resetInfiniteScroll();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        // Additional sync after all rendering is complete (only for filter removals)
        if (event && event.target && event.target.closest('facet-remove')) {
          setTimeout(() => {
            FacetFiltersForm.syncCheckboxStates(new DOMParser().parseFromString(html, 'text/html'));
          }, 100);
        }

        // Trigger infinite scroll reset after all rendering is complete
        setTimeout(() => {
          FacetFiltersForm.triggerInfiniteScrollUpdate();
        }, 50);

        document.querySelector('grid-layout-switcher').init();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    // Additional sync after all rendering is complete (only for filter removals)
    if (event && event.target && event.target.closest('facet-remove')) {
      setTimeout(() => {
        FacetFiltersForm.syncCheckboxStates(new DOMParser().parseFromString(html, 'text/html'));
      }, 100);
    }

    // Trigger infinite scroll reset after all rendering is complete
    setTimeout(() => {
      FacetFiltersForm.triggerInfiniteScrollUpdate();
    }, 50);

    document.querySelector('grid-layout-switcher').init();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static triggerInfiniteScrollUpdate() {
    if (window.infiniteScrollInitializing) {
      window.infiniteScrollInitializing = false;
      document.dispatchEvent(new CustomEvent('facet:update'));
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static resetInfiniteScroll() {
    // Clear existing infinite scroll instance
    if (window.infiniteScrollInstance) {
      window.infiniteScrollInstance = null;
    }

    // Remove existing scroll listeners
    if (window.infiniteScrollHandler) {
      window.removeEventListener('scroll', window.infiniteScrollHandler);
      window.infiniteScrollHandler = null;
    }

    // Clear any loading states
    const loadingDiv = document.querySelector('.infinite-scroll-loading');
    if (loadingDiv) {
      loadingDiv.classList.remove('loading');
    }

    // Set flag to prevent multiple initializations
    window.infiniteScrollInitializing = true;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Only sync checkbox states on filter removal (not on filter addition)
    if (event && event.target && event.target.closest('facet-remove')) {
      console.log('Filter removal detected - syncing checkboxes');
      FacetFiltersForm.syncCheckboxStates(parsedHTML);
    }

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    // Handle both mobile and desktop active facets with all possible selectors
    const activeFacetElementSelectors = ['.active-facets--mobile', '.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);

      if (activeFacetsElement && targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      } else if (targetElement) {
        // If no active facets from server, clear the container
        targetElement.innerHTML = '';
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // Update clear all button visibility
    const clearAllWrapper = document.querySelector('.clear-all-wrapper');
    const clearAllWrapperFromHTML = html.querySelector('.clear-all-wrapper');

    if (clearAllWrapper) {
      if (clearAllWrapperFromHTML) {
        clearAllWrapper.style.display = 'flex';
        clearAllWrapper.innerHTML = clearAllWrapperFromHTML.innerHTML;
      } else {
        clearAllWrapper.style.display = 'none';
      }
    }

    // Update mobile clear all button in facets pills
    const mobileClearAllWrapper = document.querySelector('.mobile-clear-all-wrapper');
    const mobileClearAllWrapperFromHTML = html.querySelector('.mobile-clear-all-wrapper');

    if (mobileClearAllWrapper && mobileClearAllWrapperFromHTML) {
      mobileClearAllWrapper.innerHTML = mobileClearAllWrapperFromHTML.innerHTML;
    } else if (mobileClearAllWrapper && !mobileClearAllWrapperFromHTML) {
      mobileClearAllWrapper.remove();
    } else if (!mobileClearAllWrapper && mobileClearAllWrapperFromHTML) {
      const facetsPillContainer = document.querySelector('.active-facets--mobile');
      if (facetsPillContainer) {
        facetsPillContainer.appendChild(mobileClearAllWrapperFromHTML);
      }
    }

    // document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static syncCheckboxStates(html) {
    // Only sync if we're not in the middle of a user interaction
    if (document.querySelector('.facets__form input:focus')) {
      return;
    }

    // Get active filters from the updated HTML
    const activeFacets = [];

    // Extract active filters from all active facet containers in the new HTML
    const activeFacetContainers = html.querySelectorAll(
      '.active-facets, .active-facets--mobile, .active-facets-mobile, .active-facets-desktop'
    );

    activeFacetContainers.forEach((container) => {
      const filterButtons = container.querySelectorAll('.active-facets__button:not(.active-facets__button--clear-all)');

      filterButtons.forEach((button) => {
        const filterText = button.querySelector('.active-facets__button-inner')?.textContent?.trim();
        if (filterText && filterText.includes(':')) {
          const [label, value] = filterText.split(':').map((s) => s.trim());
          activeFacets.push({ label, value, fullText: filterText });
        } else if (filterText && (filterText.includes('₹') || filterText.includes('$') || filterText.includes('-'))) {
          // Price range filter
          activeFacets.push({ label: 'price', value: filterText, fullText: filterText, isPrice: true });
        }
      });
    });


    // Get current DOM active facets for comparison
    const currentActiveFacets = [];
    const currentActiveFacetContainers = document.querySelectorAll(
      '.active-facets, .active-facets--mobile, .active-facets-mobile, .active-facets-desktop'
    );

    currentActiveFacetContainers.forEach((container) => {
      const filterButtons = container.querySelectorAll('.active-facets__button:not(.active-facets__button--clear-all)');
      filterButtons.forEach((button) => {
        const filterText = button.querySelector('.active-facets__button-inner')?.textContent?.trim();
        if (filterText && filterText.includes(':')) {
          const [label, value] = filterText.split(':').map((s) => s.trim());
          currentActiveFacets.push({ label, value, fullText: filterText });
        }
      });
    });

    // Only sync if there's a significant difference (like filter removal)
    if (activeFacets.length >= currentActiveFacets.length) {
      return;
    }

    // Now sync checkboxes based on server active filters
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"]');

    allCheckboxes.forEach((checkbox) => {
      const checkboxValue = checkbox.value;
      const labelText = checkbox.closest('label')?.textContent?.trim() || '';

      const shouldBeChecked = activeFacets.some((facet) => {
        return facet.value === checkboxValue || (labelText.includes(facet.value) && labelText.includes(facet.label));
      });

      if (checkbox.checked && !shouldBeChecked) {
        // Only uncheck if it should be unchecked
        checkbox.checked = false;
        console.log(`Unchecked: ${checkboxValue}`);
      }
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      console.log(searchParams);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    // Check if this is clear all button
    const isClearAll =
      event.currentTarget.href.indexOf('?') === -1 ||
      event.currentTarget.classList.contains('active-facets__button--clear-all') ||
      this.classList.contains('mobile-clear-all-wrapper');

    if (isClearAll) {
      // Clear all selected checkboxes
      this.clearAllCheckboxes();
    } else {
      // Handle individual filter removal
      this.uncheckSpecificFilter(event.currentTarget);
    }

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }

  clearAllCheckboxes() {
    // Clear all checkboxes in all forms
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]');
    allCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.checked = false;
        // Trigger change event to update visual state
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Clear radio buttons (sorting)
    const radioButtons = document.querySelectorAll('input[type="radio"][name="sort_by"]');
    radioButtons.forEach((radio) => {
      if (radio.value !== 'manual' && radio.checked) {
        // Keep default sorting
        radio.checked = false;
      }
    });

    // Clear price range inputs
    const priceInputs = document.querySelectorAll('input[name*="price"]');
    priceInputs.forEach((input) => {
      input.value = '';
    });

    console.log('All filters cleared from checkboxes');
  }

  uncheckSpecificFilter(filterLink) {
    // Extract filter information from the removal link

    // Get filter text content to identify the specific filter
    const filterText = filterLink.querySelector('.active-facets__button-inner')?.textContent?.trim();

    if (!filterText) return;

    // Check if this is a price range filter
    if (filterText.includes('₹') || filterText.includes('$') || filterText.includes('-')) {
      const priceInputs = document.querySelectorAll('input[name*="price"]');
      priceInputs.forEach((input) => {
        input.value = '';
      });
      console.log('Cleared price range inputs');
      return;
    }

    // Parse filter label and value from text (format: "Label: Value")
    const [filterLabel, filterValue] = filterText.split(':').map((s) => s.trim());

    if (!filterLabel || !filterValue) return;

    // Find and uncheck the corresponding checkbox by various methods
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"]');
    let foundMatch = false;

    allCheckboxes.forEach((checkbox) => {
      if (foundMatch) return; // Stop after finding first match

      const checkboxValue = checkbox.value;
      const checkboxName = checkbox.name;
      const labelText = checkbox.closest('label')?.textContent?.trim() || '';

      // Method 1: Direct value match
      if (checkboxValue === filterValue && checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        foundMatch = true;
        console.log(`Unchecked by direct value match: ${filterValue}`);
        return;
      }

      // Method 2: Label text contains both filter label and value
      if (labelText.includes(filterValue) && checkbox.checked) {
        // Additional check to make sure it's the right filter type
        const filterSection = checkbox.closest('.js-filter');
        if (filterSection) {
          const sectionLabel = filterSection
            .querySelector('.facets__summary-label, .mobile-facets__close-button')
            ?.textContent?.trim();
          if (sectionLabel && sectionLabel.toLowerCase().includes(filterLabel.toLowerCase())) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
            foundMatch = true;
            console.log(`Unchecked by label match: ${filterLabel}: ${filterValue}`);
            return;
          }
        }
      }

      // Method 3: URL parameter based matching
      if (checkboxName.includes('filter') && checkboxValue === filterValue && checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        foundMatch = true;
        console.log(`Unchecked by parameter match: ${checkboxName} = ${filterValue}`);
      }
    });

    if (!foundMatch) {
      console.log(`No matching checkbox found for: ${filterLabel}: ${filterValue}`);
    }
  }
}

customElements.define('facet-remove', FacetRemove);
